import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router, NavigationStart } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { RepackService } from '../repack.service';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  tap,
  of,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ProductsService } from '../../products/products.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject } from 'rxjs';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service'; // ✅ import service
import { CanComponentDeactivate } from '../../../../Guard/pending-changes.guard';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';
import { FormReloadService } from '../../../core/services/form-reload.service';
import { currentEanOf } from '../../../core/utils/current-ean.util';

@Component({
  selector: 'add-repack',
  templateUrl: './add-repack.component.html',
  styleUrls: ['./add-repack.component.scss'],
  standalone: true,
  imports: [
    CommonModule, // for *ngIf, *ngFor
    ReactiveFormsModule, // for [formGroup], formControlName
    FormsModule, // if you also use [(ngModel)]
    NgSelectModule,
  ],
})
export class AddRepackEntryComponent implements OnInit {
  // Inject services
  private fb = inject(FormBuilder);
  private repackService = inject(RepackService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private productService = inject(ProductsService);
  private confirmDialog = inject(ConfirmDialogService);
  private configService = inject(ConfigurationService);
  private formReload = inject(FormReloadService);
  // Configuration: when negative warehouse stock is permitted, "Avail for
  // pack" is shown unclamped so the operator sees how far below zero the
  // bulk parent will land after posting.
  allowNegativeStock = false;
  skuSearch$ = new Subject<string>();
  nameSearch$ = new Subject<string>();
  eanSearch$ = new Subject<string>();
  showDetailsTable = false;
  products$!: Observable<any[]>;
  repackForm!: FormGroup;
  products: any[] = [];
  pricingOptions: any[][] = [];
  selectedProducts: any[] = [];
  repackId: string | null = null;
  isEdit = false;
  repackNumber = '';
  viewOnly: boolean = false;
  navigationSubscription!: Subscription;
  selectedBulkItem: any = {
    mrp: 0,
    sellingPrice: 0,
    cost: 0,
    weight: 0,
    currentStock: 0,
    availForPack: 0,
    packed: 0,
  };
  loading = false;
  // FIX: Added property to track current search term for highlighting
  currentSearchTerm: string = '';
  childProducts: any[] = [];
  parentProduct: any = null;

  // New Pricing Modal state (for child-product MRP / Selling Price additions)
  isPricingFormVisible = false;
  editingChild: any = null;
  editingChildAcp = 0;
  editingChildCp = 0;
  newPricing: { mrp: number; sellingPrice: number } = { mrp: 0, sellingPrice: 0 };
  newPricingValidationError: string | null = null;
  ngOnInit(): void {
    this.viewOnly = this.route.snapshot.queryParamMap.get('view') === 'true';
    console.log('View Only:', this.viewOnly);
    this.configService
      .getValue(SETTING_KEYS.ALLOW_NEGATIVE_WAREHOUSE_STOCK)
      .subscribe((v) => (this.allowNegativeStock = v === true));
    this.initForm();
    this.repackId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.repackId;
    this.repackForm
      .get('bulkProduct')!
      .valueChanges.subscribe((id) => this.onBulkProductSelected(id));
    this.repackId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.repackId;
    if (this.isEdit) {
      if (this.viewOnly) {
        this.loadRepackDetails(this.repackId!); // 🔥 fetch full details in view mode
      } else {
        // load edit data if needed
        this.loadRepackDetails(this.repackId!); // you may also reuse here
      }
    } else {
      this.getNextRepackNumber();
    }
  }

  private initForm() {
    this.repackForm = this.fb.group({
      jobNumber: ['', Validators.required],
      status: ['POSTED', Validators.required],
      inputs: this.fb.array([]),
      bulkProduct: [''],
      wastagePercentage: [0.5],
      date: [new Date().toISOString().split('T')[0], Validators.required], // YYYY-MM-DD format
      costPrice: this.fb.array([]), // FormArray for cost components
      qty: [''],
      itemName: [''],
    });

    // Wastage feeds "Avail for pack", so recompute the preview when it changes.
    this.repackForm
      .get('wastagePercentage')
      ?.valueChanges.subscribe(() => this.updatePackedTotals());
  }
  private loadRepackDetails(id: string) {
    this.repackService.getRepackById(id).subscribe({
      next: (res: any) => {
        console.log('Repack details:', res);

        this.repackForm.patchValue({
          jobNumber: res.jobNumber,
          status: res.status,
          date: res.date?.split('T')[0], // keep YYYY-MM-DD
          wastagePercentage: res.wastagePercentage ?? 0.5,
        });

        // set parent product (bulk input)
        const input = res.inputs?.[0] || res.input;
        if (input) {
          // Pre-populate products for ng-select to show the label
          this.products = [
            {
              id: input.variantId,
              sku: input.skuCode,
              name: input.itemName,
              displayLabel: input.itemName,
            },
          ];
          this.repackForm.patchValue(
            { bulkProduct: input.variantId },
            { emitEvent: false }
          );

          // Use searchBySKU because variantId might not be the product ID (which causes 404)
          this.productService
            .searchBySKU(input.skuCode || input.sku)
            .pipe(
              switchMap((products: any[]) => {
                const parent = products?.[0];
                if (parent) {
                  this.parentProduct = parent;

                  // Find the specific variant if possible, otherwise callback to first or match by ID if we had the struct
                  // Ideally we match by variantId if the product has multiple variants
                  const parentVariant =
                    parent.variants?.find(
                      (v: any) => v.id === input.variantId
                    ) || parent.variants?.[0];

                  this.selectedBulkItem = {
                    variantId: input.variantId,
                    bulkCode: input.skuCode,
                    bulkItem: input.itemName,
                    weight: parentVariant?.weight || 0,
                    costPrice: input.costPrice,
                    mrp: input.mrp,
                    sellingPrice: input.sellingPrice,
                    currentStock: parentVariant?.soh || 0,
                    availForPack: +((input.qtyGm || 0) / 1000).toFixed(3),
                    packed: +((input.qtyGm || 0) / 1000).toFixed(3),
                  };

                  // Fetch child products to get master weights as fallback
                  return this.repackService.getChildProducts(parent.id);
                } else {
                  console.warn(
                    'Parent product not found for SKU:',
                    input.skuCode
                  );
                  return of({ data: [] });
                }
              })
            )
            .subscribe({
              next: (childrenRes: any) => {
                const masterChildren = childrenRes?.data || [];
                this.processOutputs(res.outputs, masterChildren);
              },
              error: (err) => {
                console.error('Failed to search product or children', err);
                this.processOutputs(res.outputs, []);
              },
            });
        } else {
          // Case where input is missing for some reason
          this.processOutputs(res.outputs, []);
        }
      },
      error: (err) => {
        console.error('Failed to fetch repack details', err);
        this.toastr.error('Failed to load repack details');
      },
    });
  }

  private processOutputs(outputs: any[], masterChildren: any[]) {
    this.childProducts = (outputs || []).map((o: any) => {
      let factor = Number(o.conversionFactor || 0);

      // Metadata match if conversionFactor is missing/zero
      if (factor === 0 && masterChildren.length > 0) {
        const match = masterChildren.find(
          (m: any) => m.sku === o.skuCode || m.variants?.[0]?.id === o.variantId
        );
        if (match) {
          const w = match.variants?.[0]?.weight || 0;
          const u = match.uom || 'kg';
          factor = this.toGrams(w, u);
        }
      }

      let weight = factor / 1000;
      let uom = 'kg';

      if (factor > 0 && factor < 1000) {
        weight = factor;
        uom = 'g';
      }

      return {
        variantId: o.variantId,
        batchNumber: o.batchNumber,
        quantity: o.packs,
        costPrice: o.costPrice,
        mrp: o.mrp,
        sellingPrice: o.sellingPrice,
        variants: [
          {
            weight: weight,
            pricing: [
              {
                costPrice: o.costPrice,
                sellingPrice: o.sellingPrice,
                mrp: o.mrp,
              },
            ],
          },
        ],
        uom: uom,
        sku: o.skuCode,
        name: o.itemName,
      };
    });
    console.log(`Child Products:`, this.childProducts);
    this.showDetailsTable = true;
  }

  // ================== GET NEXT REPACK NUMBER ==================
  private getNextRepackNumber() {
    this.repackService.getNextRepackNumber().subscribe({
      next: (res: any) => {
        if (res?.jobNumber) {
          this.repackNumber = res.jobNumber;
          this.repackForm.patchValue({ jobNumber: res.jobNumber });
        }
      },
      error: () => this.toastr.error('Failed to fetch next repack number'),
    });
  }

  removeInput(index: number) {
    this.childProducts.splice(index, 1);
    this.pricingOptions.splice(index, 1); // optional if you’re still using it
    this.repackForm.markAsDirty(); // mark form dirty for guard
  }

  /**
   * The name every product screen shows. The Products list renders
   * `longDescription` (that is the field the edit form writes), while
   * `Products.name` keeps whatever the SKU was first created as and drifts
   * after a rename. Fall back to `name` only when longDescription is blank.
   */
  displayName(product: any): string {
    return (
      String(product?.longDescription ?? '').trim() ||
      String(product?.name ?? '').trim() ||
      '-'
    );
  }

  /** ng-select binds a single label path, so precompute it on each row. */
  private withDisplayLabel(products: any[]): any[] {
    return (products || []).map((p: any) => ({
      ...p,
      displayLabel: this.displayName(p),
      // "Select by EAN" box label — current EAN of the bulk SKU, else the SKU.
      eanLabel: currentEanOf(p?.variants?.[0]) || p?.sku || '-',
    }));
  }

  searchProductBySKU(searchText: string) {
    this.currentSearchTerm = searchText; // Store current search term for highlighting
    if (searchText && searchText.length >= 2) {
      this.productService
        .genericProductSearch({ sku: searchText, context: 'REPACK_INPUT' }) // Pass context=BULK here
        .subscribe((res: any) => {
          this.products = this.withDisplayLabel(res?.data || []);
        });
    }
  }

  onBulkProductSelected(selectedItem: any) {
    this.selectedBulkItem = selectedItem;
    if (!selectedItem) {
      this.parentProduct = null;
      this.childProducts = [];
      this.selectedBulkItem = {
        mrp: 0,
        sellingPrice: 0,
        cost: 0,
        weight: 0,
        currentStock: 0,
        availForPack: 0,
        packed: 0,
      };
      return;
    }
    if (selectedItem) {
      this.repackService.getChildProducts(selectedItem).subscribe({
        next: (res: any) => {
          console.log('Child products raw response:', res);
          this.childProducts = res.data || [];
          this.childProducts.forEach((item: any) => {
            const variant = item?.variants?.[0];
            const pricing = variant?.pricing?.[0] || {};
            item.costPrice = pricing.costPrice ?? 0;
            item.sellingPrice = pricing.sellingPrice ?? 0;
            item.mrp = pricing.mrp ?? 0;
          });
          this.showDetailsTable = true;
        },
      });
    }
    this.productService.getProductById(selectedItem).subscribe({
      next: (parent: any) => {
        this.parentProduct = parent;
        const parentVariant = parent.variants?.[0];
        this.selectedBulkItem = {
          variantId: parentVariant?.id,
          bulkCode: parent.sku,
          bulkItem: this.displayName(parent),
          weight: parentVariant?.weight || 0,
          costPrice: parentVariant?.pricing?.[0]?.costPrice || 0,
          mrp: parentVariant?.pricing?.[0]?.mrp || 0,
          sellingPrice: parentVariant?.pricing?.[0]?.sellingPrice || 0,
          currentStock: parentVariant?.soh || 0,
          availForPack: Number(parentVariant?.soh || 0),
        };
      },
      error: (err) => console.error('Failed to fetch bulk item', err),
    });
  }

  onPriceChange(changedField: string, event: any) {
    const index = event.target.selectedIndex;
    const selectedPricing = this.parentProduct?.variants?.[0]?.pricing?.[index];

    if (!selectedPricing) return;

    // Update all fields based on the same index
    this.selectedBulkItem.costPrice = selectedPricing.costPrice;
    this.selectedBulkItem.sellingPrice = selectedPricing.sellingPrice;
    this.selectedBulkItem.mrp = selectedPricing.mrp;
  }

  updatePackedTotals(): void {
    let totalGrams = 0;

    for (const it of this.childProducts ?? []) {
      const qty = Number(it?.quantity) || 0;
      const weight = Number(it?.variants?.[0]?.weight) || 0; // weight per unit
      const uom = (it?.uom || '').toLowerCase();

      // Convert weight into grams based on UOM
      let unitWeightGrams = 0;
      if (it.conversionFactor && it.conversionFactor > 0) {
        unitWeightGrams = Number(it.conversionFactor);
      } else {
        const unitWeight = weight;
        unitWeightGrams = uom.startsWith('kg') ? unitWeight * 1000 : unitWeight;
      }

      // Total packed grams = sum of (qty * weight)
      totalGrams += qty * unitWeightGrams;
    }

    // Convert parent stock into grams first
    const currentStockGrams =
      (Number(this.selectedBulkItem?.currentStock) || 0) * 1000;

    // Posting consumes outputs + wastage from the parent, so the remaining
    // stock available to pack must net off the wastage too — otherwise the
    // preview overstates it by exactly the wastage amount.
    const wastagePct = +(
      this.repackForm?.get('wastagePercentage')?.value ?? 0
    );
    const wastageGrams = Math.max(0, (totalGrams * wastagePct) / 100);

    // Update packed & available for pack (display in KG). When negative
    // warehouse stock is allowed, show the true (possibly negative) balance
    // so the operator sees how far below zero posting will take the parent;
    // otherwise keep the old zero floor.
    const availKg = (currentStockGrams - totalGrams - wastageGrams) / 1000;
    this.selectedBulkItem = {
      ...(this.selectedBulkItem ?? {}),
      packed: +(totalGrams / 1000).toFixed(3),
      availForPack: +(this.allowNegativeStock
        ? availKg
        : Math.max(0, availKg)
      ).toFixed(3),
    };
  }

  /** Wastage in Kg = packed output × wastage % (same formula used on submit). */
  get wastageKg(): number {
    const packedKg = Number(this.selectedBulkItem?.packed) || 0;
    const pct = +(this.repackForm?.get('wastagePercentage')?.value ?? 0);
    return +Math.max(0, (packedKg * pct) / 100).toFixed(3);
  }

  // helpers
  private toGrams(val: number, uom?: string): number {
    const u = (uom || '').toLowerCase();
    if (u.startsWith('kg') || u.includes('kilogram'))
      return +(val * 1000).toFixed(3);
    return +val; // already grams
  }

  onProductSelectionChange(selectedItem: any, source: 'sku' | 'name' | 'ean') {
    if (!selectedItem) return;

    // if full object, extract the id safely
    const selectedId =
      typeof selectedItem === 'object' ? selectedItem.id : selectedItem;

    // Update form control explicitly
    this.repackForm.patchValue({ bulkProduct: selectedId });

    // Trigger product load logic is handled by valueChanges subscription
    // this.onBulkProductSelected(selectedId);
  }

  searchProductByName(searchText: string) {
    this.currentSearchTerm = searchText;
    if (searchText && searchText.length >= 2) {
      this.productService
        .genericProductSearch({ query: searchText, context: 'REPACK_INPUT' })
        .subscribe((res: any) => {
          this.products = this.withDisplayLabel(res?.data || []);
        });
    }
  }

  /** EAN of the bulk SKU — identity barcode or any price generation's EAN. */
  searchProductByEAN(searchText: string) {
    this.currentSearchTerm = searchText;
    const term = (searchText || '').trim();
    if (term.length >= 3) {
      this.productService
        .genericProductSearch({ ean: term, context: 'REPACK_INPUT' })
        .subscribe((res: any) => {
          this.products = this.withDisplayLabel(res?.data || []);
        });
    }
  }

  isSaved = false;

  async saveRepack() {
    const packedKg = Number(this.selectedBulkItem?.packed) || 0;
    const availKg = Number(this.selectedBulkItem?.availForPack) || 0;
    const negativeWarning =
      availKg < 0
        ? `<br/><span class="text-danger">Warehouse stock of the bulk parent will go <strong>negative: ${availKg.toFixed(
            3,
          )} Kg</strong> after posting.</span>`
        : '';
    const confirmed = await this.confirmDialog.confirm({
      title: this.isEdit ? 'Update Bulk Breakup?' : 'Submit Bulk Breakup?',
      message: `You are about to ${
        this.isEdit ? 'update' : 'submit'
      } this bulk breakup: <strong>${packedKg} Kg</strong> packed with <strong>${
        this.wastageKg
      } Kg</strong> wastage. Once posted, stock will be updated and this cannot be reversed.${negativeWarning}`,
      confirmText: this.isEdit ? 'Update' : 'Submit',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;

    const formValue = this.repackForm.value;

    // --- derive input (parent/bulk) ---
    const parentVar = this.parentProduct?.variants?.[0];
    const inputQtyGm = this.toGrams(this.selectedBulkItem?.packed || 0, 'kg'); // packed shown in Kg in UI
    const wastagePct = +(formValue.wastagePercentage ?? 0);
    const wastageQtyGm = Math.max(
      0,
      +(inputQtyGm * (wastagePct / 100)).toFixed(3)
    );

    // prices fallback from selectedBulkItem or first pricing
    const parentPricing = parentVar?.pricing?.[0] || {};
    const resolvedCost = +(
      this.selectedBulkItem?.costPrice ??
      parentPricing.costPrice ??
      0
    );
    const resolvedSP = +(
      this.selectedBulkItem?.sellingPrice ??
      parentPricing.sellingPrice ??
      0
    );
    const resolvedMRP = +(this.selectedBulkItem?.mrp ?? parentPricing.mrp ?? 0);

    const payload = {
      // The job number is assigned by the server at create. The number
      // shown on screen is a non-reserving preview, so it is not sent.
      date: formValue.date,
      notes: formValue.notes || 'string',
      status: this.isEdit ? formValue.status : 'POSTED',
      wastageQtyGm,

      input: {
        variantId:
          formValue.input?.variantId ??
          parentVar?.id ??
          this.selectedBulkItem?.variantId ??
          '',
        batchNumber: formValue.input?.batchNumber ?? '',
        qtyGm: +inputQtyGm || 0,
        costPerKg: +(formValue.input?.costPerKg ?? resolvedCost) || 0,
        costPrice: +(formValue.input?.costPrice ?? resolvedCost) || 0,
        mrp: +(formValue.input?.mrp ?? resolvedMRP) || 0,
        sellingPrice: +(formValue.input?.sellingPrice ?? resolvedSP) || 0,
      },

      outputs: (this.childProducts || []).map((item: any) => {
        const v = item?.variants?.[0];
        const firstP = v?.pricing?.[0] || {};
        let conversionFactor = 0;
        if (item.conversionFactor && item.conversionFactor > 0) {
          conversionFactor = Number(item.conversionFactor);
        } else {
          conversionFactor = this.toGrams(+v?.weight || 0, item?.uom);
        }
        return {
          variantId: v?.id ?? item?.variantId ?? '',
          batchNumber: item?.batchNumber ?? '',
          packs: +(item?.quantity ?? 0),
          conversionFactor: +conversionFactor || 0,
          costPrice: +(item?.costPrice ?? firstP.costPrice ?? 0),
          mrp: +(item?.mrp ?? firstP.mrp ?? 0),
          sellingPrice: +(item?.sellingPrice ?? firstP.sellingPrice ?? 0),
        };
      }),

      costs: [
        {
          name: 'string',
          amount: 1,
          allocateBy: 'BY_WEIGHT',
        },
      ],
    };

    if (this.isEdit) {
      this.repackService.updateRepack(this.repackId!, payload).subscribe({
        next: () => {
          this.isSaved = true; // Set flag to true
          this.toastr.success('Repack updated');
          this.router.navigate(['/repack']);
        },
        error: (err) => {
          console.error('Update error:', err);
          this.toastr.error('Update failed');
        },
      });
    } else {
      this.repackService.createRepack(payload).subscribe({
        next: () => {
          this.isSaved = true; // Set flag to true
          localStorage.removeItem('repackFormData');
          this.toastr.success('Repack created');
          // Operators break up several bulk items in a row: come back to a
          // blank form (fresh job number, no parent/children) instead of
          // the list.
          this.formReload.restart('/repack/create-repack');
        },
        error: (err) => {
          console.error('Create error:', err);
          this.toastr.error('Create failed');
        },
      });
    }
  }
  /** Header button: back to the Bulk Breakup list (guard asks if unsaved). */
  goToList(): void {
    this.router.navigate(['/repack']);
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isSaved) return true; // Bypass check if saved

    if (this.isEdit) {
      return this.confirmDialog.confirm({
        title: 'Leave Edit Page?',
        message:
          'Are you sure you want to leave <strong>Edit Repack</strong> page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
    }

    return this.confirmDialog.confirm({
      title: 'Discard changes?',
      message:
        'You have unsaved changes in <strong>Repack</strong>. Are you sure you want to leave this page?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      size: 'lg',
    });
  }

  // --- NEW PRICING MODAL (child product: add new MRP + Selling Price,
  //     keeping the existing Base Price (ACP) and Cost Price (CP) unchanged) ---
  openNewPricingForm(item: any): void {
    if (this.viewOnly || !item) return;
    this.editingChild = item;

    const cp = Number(item.costPrice || 0);
    this.editingChildCp = cp;

    // Find the existing pricing row that matches the current cost price so we
    // can preserve its base price. Fallback to first pricing entry or cp.
    const pricingList: any[] = item?.variants?.[0]?.pricing || [];
    const matching = pricingList.find((p: any) => Number(p.costPrice) === cp);
    const fallback = pricingList[0] || {};
    this.editingChildAcp = Number(matching?.basePrice ?? fallback?.basePrice ?? cp);

    this.newPricing = { mrp: 0, sellingPrice: 0 };
    this.newPricingValidationError = null;
    this.isPricingFormVisible = true;
  }

  validateNewPricing(): boolean {
    // Compare at 3 decimals so float noise in a derived CP can't fail an
    // equal-looking pair (e.g. 84.50000001 vs 84.5).
    const round3 = (v: number) => Math.round(v * 1000) / 1000;
    const mrp = round3(Number(this.newPricing.mrp) || 0);
    const sp = round3(Number(this.newPricing.sellingPrice) || 0);
    const cp = round3(this.editingChildCp);

    if (mrp <= 0 || sp <= 0) {
      this.newPricingValidationError = 'MRP and Selling Price must be positive values.';
      return false;
    }
    if (mrp < cp) {
      this.newPricingValidationError = 'MRP should be greater than or equal to Cost Price.';
      return false;
    }
    if (sp < cp) {
      this.newPricingValidationError = 'Selling Price should be greater than or equal to Cost Price.';
      return false;
    }
    if (sp > mrp) {
      this.newPricingValidationError = 'Selling Price cannot exceed MRP.';
      return false;
    }
    this.newPricingValidationError = null;
    return true;
  }

  saveNewPricing(): void {
    if (!this.editingChild || !this.validateNewPricing()) return;

    const variantId =
      this.editingChild?.variants?.[0]?.id || this.editingChild?.variantId;
    if (!variantId) {
      this.toastr.error('Variant ID missing for this child product.');
      return;
    }

    const body = {
      variantId,
      basePrice: this.editingChildAcp,
      costPrice: this.editingChildCp,
      sellingPrice: Number(this.newPricing.sellingPrice),
      mrp: Number(this.newPricing.mrp),
      validFrom: new Date().toISOString(),
    };

    this.productService.addVariantPricing(body).subscribe({
      next: (res: any) => {
        this.toastr.success('New pricing added successfully!', 'Success');

        // Insert into the local pricing array so the row's MRP / Selling Price
        // dropdowns immediately see the new option.
        if (!this.editingChild.variants) this.editingChild.variants = [{}];
        if (!this.editingChild.variants[0]) this.editingChild.variants[0] = {};
        if (!this.editingChild.variants[0].pricing) {
          this.editingChild.variants[0].pricing = [];
        }
        this.editingChild.variants[0].pricing.unshift({
          basePrice: res?.basePrice ?? body.basePrice,
          costPrice: res?.costPrice ?? body.costPrice,
          sellingPrice: res?.sellingPrice ?? body.sellingPrice,
          mrp: res?.mrp ?? body.mrp,
        });

        // Auto-select the new MRP / Selling Price on the row (CP is unchanged).
        this.editingChild.mrp = body.mrp;
        this.editingChild.sellingPrice = body.sellingPrice;

        this.isPricingFormVisible = false;
        this.editingChild = null;
      },
      error: (err) => {
        console.error('Error adding pricing:', err);
        this.toastr.error('Failed to add new pricing.', 'Error');
      },
    });
  }
}
